import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Phone, CheckCircle, Rocket, ArrowRight, PartyPopper } from "lucide-react";
import confetti from "canvas-confetti";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

export default function OnboardingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fire confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    // Get Stripe session ID from URL
    const stripeSessionId = searchParams.get("session_id");
    const sessionToken = localStorage.getItem("onboarding_session");
    
    if (stripeSessionId) {
      // Verify the checkout with backend
      verifyCheckout(stripeSessionId);
    } else if (sessionToken) {
      // Just show success, account creation happens via webhook
      setLoading(false);
      setAccountData({
        email: localStorage.getItem("onboarding_email") || "your email",
        plan: localStorage.getItem("onboarding_plan") || "ai_receptionist"
      });
    } else {
      setError("No session found");
      setLoading(false);
    }
  }, [searchParams]);

  const verifyCheckout = async (stripeSessionId) => {
    try {
      // In production, you'd verify with your backend
      // For now, we just show success
      setAccountData({
        email: localStorage.getItem("onboarding_email") || "your email",
        plan: localStorage.getItem("onboarding_plan") || "ai_receptionist",
        stripeSessionId
      });
      
      // Clear onboarding session data
      localStorage.removeItem("onboarding_session");
      localStorage.removeItem("onboarding_plan");
      localStorage.removeItem("email_verification_token");
    } catch (err) {
      setError("Failed to verify checkout");
    }
    setLoading(false);
  };

  const handleContinue = () => {
    // Redirect to login or directly to onboarding wizard
    navigate("/login?onboarding=complete");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Setting up your account...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <Card className="bg-slate-900 border-slate-800 max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
            <p className="text-slate-400 mb-6">{error}</p>
            <Button onClick={() => navigate("/pricing")} className="bg-blue-500 hover:bg-blue-600">
              Start Over
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-2">
            <Phone className="w-8 h-8 text-blue-500" />
            <span className="text-xl font-bold">Speakr</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-16 max-w-2xl">
        <div className="text-center">
          {/* Success Icon */}
          <div className="relative inline-block mb-8">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
            <div className="absolute -top-2 -right-2">
              <PartyPopper className="w-8 h-8 text-yellow-400" />
            </div>
          </div>

          <h1 className="text-4xl font-bold mb-4">
            Welcome to Speakr! 🎉
          </h1>
          <p className="text-xl text-slate-400 mb-8">
            Your account has been created successfully.<br />
            Your {accountData?.plan === "ai_sales_agent" ? "14" : "14"}-day free trial starts now!
          </p>

          {/* Next Steps Card */}
          <Card className="bg-slate-900 border-slate-800 text-left mb-8">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-blue-400" />
                Get started in 3 simple steps
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <p className="font-medium">Check your email</p>
                    <p className="text-sm text-slate-400">
                      We sent your login credentials to {accountData?.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <p className="font-medium">Complete your AI agent setup</p>
                    <p className="text-sm text-slate-400">
                      Our wizard will guide you through configuring your voice agent
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <p className="font-medium">Connect your phone number</p>
                    <p className="text-sm text-slate-400">
                      Choose a local or toll-free number for your AI agent
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Button 
            size="lg"
            className="bg-blue-500 hover:bg-blue-600 h-14 px-8 text-lg"
            onClick={handleContinue}
            data-testid="continue-to-setup"
          >
            Continue to Setup
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          {/* Support */}
          <p className="text-sm text-slate-500 mt-8">
            Need help? Our team is here for you.<br />
            <a href="mailto:info@gskproductions.ca" className="text-blue-400 hover:underline">
              info@gskproductions.ca
            </a>
            {" "}•{" "}
            <a href="tel:+12893356542" className="text-blue-400 hover:underline">
              +1 (289) 335-6542
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
